import React, { Suspense, lazy, useEffect, useState } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useParams,
  useLocation,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./auth/AuthProvider";
import ScreenBoundary from "./components/ScreenBoundary";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import VerifyEmail from "./pages/VerifyEmail";
import { ThemeProvider, useTheme } from "./theme/ThemeContext";
import "./App.css";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const ProjectsList = lazy(() => import("./pages/ProjectsList"));
const ProjectDetail = lazy(() => import("./pages/ProjectDetail"));
const CompliancePage = lazy(() => import("./pages/CompliancePage"));
const Rulesets = lazy(() => import("./pages/Rulesets"));
const ValueLists = lazy(() => import("./pages/ValueLists"));
const Admin = lazy(() => import("./pages/Admin"));
const OrgAdmin = lazy(() => import("./pages/OrgAdmin"));
const Settings = lazy(() => import("./pages/Settings"));
const ModelViewer3D = lazy(() => import("./pages/ModelViewer3D"));
const StandardsPage = lazy(() => import("./components/Standards/StandardsPage"));
const ImportReviewPage = lazy(() => import("./components/Standards/ImportReviewPage"));
const RulesetDetailPage = lazy(() => import("./components/Standards/RulesetDetailPage"));

function AuthGate({ children, perm }) {
  const { isReady, isAuthenticated, hasPermission } = useAuth();
  if (!isReady) return <div className="auth-page" />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (perm && !hasPermission(perm)) return <Navigate to="/projects" replace />;
  return children;
}

function LoginRoute() {
  const { isReady, isAuthenticated } = useAuth();
  if (!isReady) return <div className="auth-page" />;
  if (isAuthenticated) return <Navigate to="/projects" replace />;
  return <Login />;
}

function LegacyDetailRedirect({ prefix }) {
  const { id } = useParams();
  return <Navigate to={`${prefix}/${id}`} replace />;
}

function AppToastContainer() {
  const { theme } = useTheme();
  return (
    <ToastContainer
      position="bottom-right"
      autoClose={4000}
      hideProgressBar={false}
      newestOnTop={false}
      closeOnClick
      rtl={false}
      pauseOnFocusLoss
      draggable
      pauseOnHover
      theme={theme}
      containerStyle={{
        width: "90%",
        maxWidth: "400px",
        left: "50%",
        transform: "translateX(-50%)",
      }}
    />
  );
}

function AppRoutes() {
  const { user, logout, isAuthenticated, isReady, hasPermission } = useAuth();
  const location = useLocation();

  // The 3D viewer is expensive to (re)load — models download, IFC conversion,
  // WebGL scene. Mount it on first visit and keep it alive (hidden) across
  // navigation so models, camera and filters survive page switches. It shows
  // on the legacy /viewer-3d deep-link route AND as a project's "3D View" tab.
  const viewerActive =
    location.pathname === "/viewer-3d" ||
    /^\/projects\/[^/]+\/3d\/?$/.test(location.pathname);
  const [viewerMounted, setViewerMounted] = useState(false);
  // Latest "open this model in 3D" request from elsewhere in the app
  // (e.g. picking a model in the Elements view). The viewer consumes it.
  const [viewerOpenRequest, setViewerOpenRequest] = useState(null);
  // Project the viewer is currently scoped to (set by the project detail page's
  // 3D tab) — lets the viewer render the project tab strip with a real name.
  const [viewerProject, setViewerProject] = useState(null);
  useEffect(() => {
    if (viewerActive && isAuthenticated && hasPermission("data:read")) {
      setViewerMounted(true);
    }
  }, [viewerActive, isAuthenticated, hasPermission]);
  useEffect(() => {
    const allowed = () => isAuthenticated && hasPermission("data:read");
    // Opening a model page prewarms the viewer: the scene, the renderer and
    // the 3.5 MB fragments worker start coming up while the user is still on
    // another tab, so the 3D tab has nothing left to boot when it's clicked.
    const prewarm = () => {
      if (allowed()) setViewerMounted(true);
    };
    const handler = (e) => {
      if (!allowed()) return;
      if (!e.detail?.modelId) return;
      // Mount on demand too — the geometry preload is dispatched from the
      // Elements and Quality tabs, where the 3D route is not active.
      setViewerMounted(true);
      setViewerOpenRequest({ ...e.detail, ts: Date.now() });
      if (e.detail.project) setViewerProject(e.detail.project);
    };
    window.addEventListener("bimcheck:prewarm-3d", prewarm);
    window.addEventListener("bimcheck:open-model-3d", handler);
    return () => {
      window.removeEventListener("bimcheck:prewarm-3d", prewarm);
      window.removeEventListener("bimcheck:open-model-3d", handler);
    };
  }, [isAuthenticated, hasPermission]);
  useEffect(() => {
    // Drop the scene on logout so the next user starts clean.
    if (!isAuthenticated) setViewerMounted(false);
  }, [isAuthenticated]);

  if (!isReady) return <div className="auth-page" />;
  return (
    <>
    <ScreenBoundary resetKey={location.pathname}><Suspense fallback={<div role="status" className="projects-empty">Loading view…</div>}>
    <Routes>
      <Route
        path="/"
        element={<Navigate to={isAuthenticated ? "/projects" : "/login"} replace />}
      />

      {/* Public auth routes */}
      <Route path="/login" element={<LoginRoute />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />

      <Route
        path="/projects"
        element={
          <AuthGate perm="data:read">
            <ProjectsList user={user} onLogout={logout} />
          </AuthGate>
        }
      />

      {/* Project detail: info + access, with quality / elements / 3d tabs.
          The 3D tab renders through the keep-alive container below. */}
      <Route
        path="/projects/:projectId/*"
        element={
          <AuthGate perm="data:read">
            <ProjectDetail user={user} onLogout={logout} />
          </AuthGate>
        }
      />

      {/* The page itself renders through the keep-alive container below;
          the route only enforces auth/permission redirects. */}
      <Route
        path="/viewer-3d"
        element={
          <AuthGate perm="data:read">
            <></>
          </AuthGate>
        }
      />

      {/* Compliance area (Standards / Rulesets / Value Lists) */}
      <Route
        path="/compliance"
        element={
          <AuthGate>
            <CompliancePage user={user} onLogout={logout} />
          </AuthGate>
        }
      >
        <Route index element={<Navigate to="standards" replace />} />
        <Route path="standards" element={<StandardsPage />} />
        <Route path="rulesets" element={<Rulesets />} />
        <Route path="value-lists" element={<ValueLists />} />
      </Route>
      <Route
        path="/compliance/imports/:id"
        element={
          <AuthGate>
            <ImportReviewPage user={user} onLogout={logout} />
          </AuthGate>
        }
      />
      <Route
        path="/compliance/rulesets/:id"
        element={
          <AuthGate>
            <RulesetDetailPage user={user} onLogout={logout} />
          </AuthGate>
        }
      />

      {/* Administration */}
      <Route
        path="/admin"
        element={
          <AuthGate perm="admin:manage">
            <Admin user={user} onLogout={logout} />
          </AuthGate>
        }
      />
      <Route
        path="/org-admin"
        element={
          <AuthGate perm="org:manage">
            <OrgAdmin user={user} onLogout={logout} />
          </AuthGate>
        }
      />

      {/* Personal preferences */}
      <Route
        path="/settings"
        element={
          <AuthGate>
            <Settings user={user} onLogout={logout} />
          </AuthGate>
        }
      />

      {/* Legacy redirects — keep external bookmarks working */}
      <Route path="/dashboard/*" element={<Navigate to="/projects" replace />} />
      <Route path="/elements/*" element={<Navigate to="/projects" replace />} />
      <Route path="/rulesets" element={<Navigate to="/compliance/rulesets" replace />} />
      <Route path="/value-lists" element={<Navigate to="/compliance/value-lists" replace />} />
      <Route path="/standards" element={<Navigate to="/compliance/standards" replace />} />
      <Route
        path="/standards/imports/:id"
        element={<LegacyDetailRedirect prefix="/compliance/imports" />}
      />
      <Route
        path="/standards/rulesets/:id"
        element={<LegacyDetailRedirect prefix="/compliance/rulesets" />}
      />

      <Route
        path="*"
        element={<Navigate to={isAuthenticated ? "/projects" : "/login"} replace />}
      />
    </Routes>
    </Suspense></ScreenBoundary>

    {viewerMounted && (
      <div className={`viewer-keepalive${viewerActive ? " active" : ""}`}>
        <ScreenBoundary resetKey={viewerProject?.id}><Suspense fallback={<div role="status">Loading 3D viewer…</div>}>
        <ModelViewer3D
          active={viewerActive}
          openRequest={viewerOpenRequest}
          projectContext={viewerProject}
        />
        </Suspense></ScreenBoundary>
      </div>
    )}
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AppToastContainer />
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
